import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { KPICard } from "@/components/collections/kpi-card";
import {
  Activity,
  TrendingUp,
  Clock,
  DollarSign,
  AlertTriangle,
  Info,
  CheckCircle2,
  Users,
  BarChart3,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { generateMockCases, generateMockTeamPerformance, generateMockAlerts } from "@/lib/mock-data/collections-mock-data";

export default function CollectionsDashboard() {
  const [isLoading] = useState(false);
  const [cases] = useState(generateMockCases());
  const [teamPerformance] = useState(generateMockTeamPerformance());
  const [alerts] = useState(generateMockAlerts());
  
  // Calculate dashboard metrics
  const dashboardData = {
    overview: {
      totalActiveCases: cases.filter(c => c.status === 'Active').length,
      totalOutstanding: cases.reduce((sum, c) => sum + c.amount, 0),
      collectionRate: 78.5,
      avgDaysToPayment: 18,
    },
    teamPerformance,
    alerts,
    weeklyTrends: [
      { week: 'Week 1', collected: 125000 },
      { week: 'Week 2', collected: 138000 },
      { week: 'Week 3', collected: 142000 },
      { week: 'Week 4', collected: 135000 },
      { week: 'Week 5', collected: 148000 },
      { week: 'Week 6', collected: 152000 },
      { week: 'Week 7', collected: 145000 },
      { week: 'Week 8', collected: 158000 },
    ],
    channelPerformance: [
      { channel: 'Email', responseRate: 68, totalSent: 450, responses: 306 },
      { channel: 'Phone', responseRate: 82, totalSent: 320, responses: 262 },
      { channel: 'SMS', responseRate: 45, totalSent: 280, responses: 126 },
      { channel: 'Portal', responseRate: 91, totalSent: 150, responses: 137 },
    ],
    casesByStatus: [
      { status: 'Active', count: cases.filter(c => c.status === 'Active').length, totalValue: cases.filter(c => c.status === 'Active').reduce((sum, c) => sum + c.amount, 0) },
      { status: 'Pending', count: 32, totalValue: 485000 },
      { status: 'Resolved', count: 58, totalValue: 1250000 },
      { status: 'Disputed', count: 12, totalValue: 185000 },
    ],
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Collections Dashboard</h1>
        <p className="text-muted-foreground">Monitor collection performance and team metrics</p>
      </div>

      {/* Overview Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Active Cases"
          value={dashboardData.overview.totalActiveCases}
          subtitle="Across all team members"
          icon={Activity}
          colorScheme="blue"
          isLoading={isLoading}
        />
        <KPICard
          title="Total Outstanding"
          value={`$${dashboardData.overview.totalOutstanding.toLocaleString()}`}
          subtitle="In active collection"
          icon={DollarSign}
          colorScheme="purple"
          isLoading={isLoading}
        />
        <KPICard
          title="Collection Rate"
          value={`${dashboardData.overview.collectionRate}%`}
          subtitle="Last 30 days"
          icon={TrendingUp}
          trend={{ value: "+2.5% from last month", isPositive: true }}
          colorScheme="green"
          isLoading={isLoading}
        />
        <KPICard
          title="Avg Days to Payment"
          value={`${dashboardData.overview.avgDaysToPayment} days`}
          subtitle="Across all cases"
          icon={Clock}
          trend={{ value: "-3 days improvement", isPositive: true }}
          colorScheme="amber"
          isLoading={isLoading}
        />
      </div>

      {/* Urgent Items Alert Section */}
      <Card className="border-l-4 border-l-amber-500 bg-gradient-to-br from-amber-50/30 to-transparent dark:from-amber-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            Items Requiring Attention
          </CardTitle>
          <CardDescription>Critical items that need immediate action</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {dashboardData.alerts.map((alert) => (
              <Link key={alert.id} to={alert.link}>
                <div className="flex items-start justify-between rounded-lg border p-3 transition-colors hover:bg-muted/50">
                  <div className="flex items-start gap-3">
                    {alert.type === "urgent" && <AlertTriangle className="mt-0.5 h-4 w-4 text-red-600" />}
                    {alert.type === "warning" && <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />}
                    {alert.type === "info" && <Info className="mt-0.5 h-4 w-4 text-blue-600" />}
                    <div>
                      <p className="font-medium">{alert.title}</p>
                      <p className="text-sm text-muted-foreground">{alert.description}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabbed Content */}
      <Tabs defaultValue="team" className="space-y-4">
        <TabsList>
          <TabsTrigger value="team" className="gap-2">
            <Users className="h-4 w-4" />
            Team Performance
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Trends & Analytics
          </TabsTrigger>
          <TabsTrigger value="status" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Cases by Status
          </TabsTrigger>
        </TabsList>

        {/* Team Performance Tab */}
        <TabsContent value="team" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Team Member Performance</CardTitle>
              <CardDescription>Individual metrics and active workload</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {dashboardData.teamPerformance.map((member) => (
                  <div key={member.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                          {member.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {member.activeCases} active cases • {member.resolvedCases} resolved this month
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${member.totalCollected.toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground">collected</p>
                      </div>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3">
                      <div>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Success Rate</span>
                          <span className="font-medium">{member.successRate}%</span>
                        </div>
                        <Progress value={member.successRate} className="h-2" />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Avg Response Time</span>
                          <span className="font-medium">{member.avgResponseTime}h</span>
                        </div>
                        <Progress value={(24 - member.avgResponseTime) * 4} className="h-2" />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Overdue Actions</span>
                          <Badge variant={member.overdueActions > 0 ? "destructive" : "secondary"}>
                            {member.overdueActions}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends & Analytics Tab */}
        <TabsContent value="trends" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Weekly Collections Trend</CardTitle>
                <CardDescription>Collections over the past 8 weeks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboardData.weeklyTrends.map((week, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="w-16 text-sm text-muted-foreground">{week.week}</span>
                      <Progress value={(week.collected / 150000) * 100} className="h-3 flex-1" />
                      <span className="w-24 text-right text-sm font-medium">
                        ${week.collected.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Response Rates by Channel</CardTitle>
                <CardDescription>Customer engagement across communication channels</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {dashboardData.channelPerformance.map((channel) => (
                    <div key={channel.channel} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{channel.channel}</span>
                        <span className="text-sm text-muted-foreground">{channel.responseRate}%</span>
                      </div>
                      <Progress value={channel.responseRate} className="h-2" />
                      <p className="text-xs text-muted-foreground">
                        {channel.totalSent} sent • {channel.responses} responses
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Cases by Status Tab */}
        <TabsContent value="status" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {dashboardData.casesByStatus.map((status) => (
              <Card key={status.status}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">{status.status}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold">{status.count}</div>
                    <p className="text-sm text-muted-foreground">
                      ${status.totalValue.toLocaleString()} total value
                    </p>
                    <Progress value={(status.count / 127) * 100} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Status Distribution</CardTitle>
              <CardDescription>Breakdown of cases by current status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData.casesByStatus.map((status) => (
                  <div key={status.status} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge
                        variant={
                          status.status === "Active"
                            ? "default"
                            : status.status === "Pending"
                            ? "secondary"
                            : status.status === "Resolved"
                            ? "outline"
                            : "destructive"
                        }
                      >
                        {status.status}
                      </Badge>
                      <span className="text-sm">{status.count} cases</span>
                    </div>
                    <span className="text-sm font-medium">${status.totalValue.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/collections/queue">View My Cases</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/collections/promises">Manage Promises</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/settings/strategies">Configure Strategies</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/customers/ageing-analysis">View Ageing Report</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
